import React, { useEffect, useRef, useState } from 'react';
import { Bold, Italic, Underline, Receipt, User, Users as UsersIcon, Loader2, Building2, Paperclip, X } from 'lucide-react';
import { moduleApi } from '../../lib/api';

export interface NotificationAttachment { name: string; data: string; }

const MAX_NOTIFICATION_ATTACHMENT_BYTES = 4 * 1024 * 1024;

// Même logique que les autres pièces jointes de l'app (modèles de Démarche client) : une image est
// recompressée côté navigateur pour éviter un base64 énorme, un PDF est simplement plafonné en taille.
const compressNotificationImage = (file: File, maxDim = 1200, quality = 0.8): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('Lecture du fichier impossible'));
    reader.onload = () => {
        const img = new Image();
        img.onerror = () => reject(new Error('Image invalide'));
        img.onload = () => {
            const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
            const w = Math.max(1, Math.round(img.width * scale));
            const h = Math.max(1, Math.round(img.height * scale));
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            if (!ctx) { reject(new Error('Canvas non supporté')); return; }
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
});
const readNotificationFileAsDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('Lecture du fichier impossible'));
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(file);
});

/** Bouton compact "joindre un fichier" (PDF/image) réutilisé dans chaque bloc d'email — un seul fichier par
 *  type d'email, envoyé en pièce jointe à chaque envoi de cet email. */
export const NotificationAttachmentField: React.FC<{
    attachment: NotificationAttachment | null;
    onChange: (att: NotificationAttachment | null) => void;
}> = ({ attachment, onChange }) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setError('');
        setUploading(true);
        const task = file.type.startsWith('image/')
            ? compressNotificationImage(file)
            : (file.size > MAX_NOTIFICATION_ATTACHMENT_BYTES
                ? Promise.reject(new Error(`Ce fichier dépasse ${Math.round(MAX_NOTIFICATION_ATTACHMENT_BYTES / (1024 * 1024))} Mo — merci d'en choisir un plus léger.`))
                : readNotificationFileAsDataUrl(file));
        task
            .then((dataUrl) => onChange({ name: file.name, data: dataUrl }))
            .catch((err) => setError(err?.message || 'Impossible de traiter ce fichier.'))
            .finally(() => setUploading(false));
    };

    return (
        <div className="space-y-1">
            <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border bg-surface-soft text-content-muted hover:text-content text-[11px] font-bold cursor-pointer transition-all">
                    {uploading ? <Loader2 size={11} className="animate-spin" /> : <Paperclip size={11} />}
                    {uploading ? 'Chargement...' : (attachment ? attachment.name : 'Joindre un fichier (PDF/image)')}
                    <input type="file" accept="application/pdf,image/*" className="hidden" onChange={handleFileChange} disabled={uploading} />
                </label>
                {attachment && (
                    <button type="button" onClick={() => onChange(null)} title="Retirer la pièce jointe"
                        className="p-1 rounded-md text-content-muted hover:text-red-400 transition-all"><X size={12} /></button>
                )}
            </div>
            {error && <p className="text-[11px] text-red-400 font-semibold">{error}</p>}
        </div>
    );
};

interface NotificationToken { token: string; label: string; }
// Toutes les variables disponibles, dans toutes les sections (rappel salarié/client, assignation, confirmation,
// facture) — certaines n'ont de sens que dans certains emails, mais toutes sont utilisables partout côté serveur.
// Labels volontairement suffixés (client/entreprise/salarié assigné) pour ne jamais prêter à confusion entre deux
// variables proches (ex: adresse client vs adresse entreprise, numéro client vs numéro entreprise).
export const ALL_NOTIFICATION_TOKENS: NotificationToken[] = [
    { token: '{{client_nom}}', label: 'Nom client' },
    { token: '{{client_telephone}}', label: 'Numéro client' },
    { token: '{{client_email}}', label: 'Email client' },
    { token: '{{adresse}}', label: 'Adresse client' },
    { token: '{{lieu_rdv}}', label: 'Lieu du rendez-vous (domicile/entreprise)' },
    { token: '{{salarie_prenom}}', label: 'Prénom salarié assigné' },
    { token: '{{salarie_nom}}', label: 'Nom salarié assigné' },
    { token: '{{salarie_nom_complet}}', label: 'Nom complet salarié assigné' },
    { token: '{{salarie_telephone}}', label: 'Numéro salarié assigné' },
    { token: '{{salarie_email}}', label: 'Email salarié assigné' },
    { token: '{{salaries}}', label: 'Tous les intervenants' },
    { token: '{{entreprise}}', label: 'Nom entreprise' },
    { token: '{{entreprise_telephone}}', label: 'Numéro entreprise' },
    { token: '{{entreprise_email}}', label: 'Email entreprise' },
    { token: '{{entreprise_adresse}}', label: 'Adresse entreprise' },
    { token: '{{entreprise_tva}}', label: 'TVA entreprise' },
    { token: '{{entreprise_siret}}', label: 'SIRET entreprise' },
    { token: '{{date}}', label: 'Date du rendez-vous' },
    { token: '{{horaire}}', label: 'Horaire du rendez-vous (plage)' },
    { token: '{{heure}}', label: 'Heure du rendez-vous (début)' },
    { token: '{{prestation}}', label: 'Nom de la prestation' },
    { token: '{{prestation_prix}}', label: 'Prix de la prestation' },
    { token: '{{prestation_duree}}', label: 'Durée de la prestation' },
    { token: '{{statut_paiement}}', label: 'Statut de paiement (résumé)' },
    { token: '{{total}}', label: 'Total facture' },
    { token: '{{lien_paiement}}', label: 'Lien de paiement Stripe (si configuré)' },
    { token: '{{lien_rdv}}', label: 'Bouton "Prendre RDV" (cliquez où l\'insérer)' },
    { token: '{{deja_regle}}', label: 'Montant déjà réglé' },
    { token: '{{reste_a_payer}}', label: 'Reste à payer' },
    { token: '{{moyen_paiement}}', label: 'Moyen de paiement' },
    { token: '{{date_echeance}}', label: 'Date limite de paiement' },
    { token: '{{montant_regle}}', label: 'Montant réglé' },
    { token: '{{motif}}', label: "Motif de l'absence" },
    { token: '{{logo}}', label: 'Votre logo' },
];

/** Insère un jeton {{...}} à la position du curseur dans le champ (input/textarea) actuellement ciblé,
 *  au lieu d'obliger l'utilisateur à copier-coller le code à la main. */
export const insertNotificationToken = (
    el: HTMLInputElement | HTMLTextAreaElement | null,
    value: string,
    setValue: (v: string) => void,
    token: string,
) => {
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    setValue(next);
    if (el) {
        requestAnimationFrame(() => {
            el.focus();
            const pos = start + token.length;
            el.setSelectionRange(pos, pos);
        });
    }
};

/** Entoure le texte sélectionné (ou insère une paire vide au curseur) avec un marqueur Markdown
 *  (** gras, * italique, __ souligné, [[taille=Npx]]...[[/taille]] taille de police) — interprété
 *  côté serveur par templateBodyToHtml à l'envoi. `closeMarker` par défaut identique à `openMarker`
 *  (cas symétrique gras/italique/souligné) ; distinct pour la taille de police. */
export const wrapNotificationSelection = (
    el: HTMLTextAreaElement | null,
    value: string,
    setValue: (v: string) => void,
    openMarker: string,
    closeMarker: string = openMarker,
) => {
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const selected = value.slice(start, end);
    const next = value.slice(0, start) + openMarker + selected + closeMarker + value.slice(end);
    setValue(next);
    if (el) {
        requestAnimationFrame(() => {
            el.focus();
            const pos = selected ? start + openMarker.length + selected.length + closeMarker.length : start + openMarker.length;
            el.setSelectionRange(pos, pos);
        });
    }
};

/** Options de taille de police proposées dans le sélecteur du bloc email — valeurs en px interprétées
 *  par templateBodyToHtml via le marqueur [[taille=Npx]]...[[/taille]]. */
const FONT_SIZE_OPTIONS = [
    { value: '12', label: 'Petit' },
    { value: '16', label: 'Normal' },
    { value: '20', label: 'Grand' },
    { value: '28', label: 'Très grand' },
];

/** Un bloc "objet + corps + gras/italique + variables" générique, réutilisé pour chaque email. */
export function NotificationBlock({
    title, toggle, subject, setSubject, body, setBody, subjectRef, bodyRef, activeField, setActiveField, onInsertToken, onWrap, tokens,
    attachment, onAttachmentChange,
}: {
    title: string;
    toggle?: { checked: boolean; onChange: (v: boolean) => void };
    subject: string;
    setSubject: (v: string) => void;
    body: string;
    setBody: (v: string) => void;
    subjectRef: React.RefObject<HTMLInputElement>;
    bodyRef: React.RefObject<HTMLTextAreaElement>;
    activeField: 'subject' | 'body';
    setActiveField: (f: 'subject' | 'body') => void;
    onInsertToken: (token: string) => void;
    onWrap: (marker: string) => void;
    tokens?: NotificationToken[];
    attachment?: NotificationAttachment | null;
    onAttachmentChange?: (att: NotificationAttachment | null) => void;
}) {
    return (
        <div className="space-y-3 p-5 rounded-2xl bg-surface border border-border">
            {toggle ? (
                <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm font-bold text-content">{title}</span>
                    <input type="checkbox" checked={toggle.checked} onChange={(e) => toggle.onChange(e.target.checked)} className="accent-brand-600 w-4 h-4" />
                </label>
            ) : (
                <span className="text-sm font-bold text-content">{title}</span>
            )}
            <input ref={subjectRef} value={subject} onChange={(e) => setSubject(e.target.value)}
                onFocus={() => setActiveField('subject')} placeholder="Objet de l'email"
                className="w-full bg-surface-soft border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
            <div className="flex items-center gap-1">
                <button type="button" onClick={() => onWrap('**')} title="Gras" className="p-1.5 rounded-md border border-border bg-surface-soft text-content-muted hover:text-content"><Bold size={12} /></button>
                <button type="button" onClick={() => onWrap('*')} title="Italique" className="p-1.5 rounded-md border border-border bg-surface-soft text-content-muted hover:text-content"><Italic size={12} /></button>
                {/* Souligné et taille de police passent directement par bodyRef/body/setBody (déjà reçus en
                    props) plutôt que par onWrap : onWrap est un handler à un seul marqueur défini côté appelant
                    pour gras/italique (symétrique), incompatible avec la paire ouverture/fermeture distincte
                    qu'exige la taille de police — éviter de devoir mettre à jour tous les appelants existants. */}
                <button type="button" onClick={() => wrapNotificationSelection(bodyRef.current, body, setBody, '__')} title="Souligné" className="p-1.5 rounded-md border border-border bg-surface-soft text-content-muted hover:text-content"><Underline size={12} /></button>
                <select defaultValue="" title="Taille du texte sélectionné"
                    onChange={(e) => { if (e.target.value) { wrapNotificationSelection(bodyRef.current, body, setBody, `[[taille=${e.target.value}px]]`, '[[/taille]]'); e.target.value = ''; } }}
                    className="p-1.5 rounded-md border border-border bg-surface-soft text-content-muted hover:text-content text-[11px] outline-none">
                    <option value="" disabled>Taille</option>
                    {FONT_SIZE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
            </div>
            <textarea ref={bodyRef} value={body} onChange={(e) => setBody(e.target.value)}
                onFocus={() => setActiveField('body')} placeholder="Corps de l'email" rows={10}
                className="w-full bg-surface-soft border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500 resize-none" />
            {onAttachmentChange && (
                <NotificationAttachmentField attachment={attachment ?? null} onChange={onAttachmentChange} />
            )}
            <div>
                <p className="text-[10px] text-content-muted mb-1.5">Cliquez pour insérer une variable dans le champ ciblé :</p>
                <div className="flex flex-wrap gap-1.5">
                    {(tokens || ALL_NOTIFICATION_TOKENS).map((t) => (
                        <button key={t.token} type="button" onClick={() => onInsertToken(t.token)}
                            className="px-2 py-1 rounded-md border border-border bg-surface-soft text-content-muted hover:text-brand-500 hover:border-brand-500 text-[10px] font-semibold transition-all">
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

/** Onglet "Notifications" de la page Paramètres centrale (visible uniquement pour les clients Prestation) —
 *  personnalisation des emails automatiques du module Planning rendez-vous. */
export const PrestationNotificationsSettings: React.FC<{ activeModules?: string[] }> = ({ activeModules = [] }) => {
    const showFacture = activeModules.includes('facture');
    // Certains emails ne concernent que des fonctionnalités liées à un module précis — inutile de les
    // proposer si ce module n'est pas actif pour ce client, ça n'arriverait jamais.
    const showDemarches = activeModules.includes('crm');
    const showConges = activeModules.includes('demande_conges');
    const [recipient, setRecipient] = useState<'client' | 'salarie' | 'etablissement'>('client');

    const [notifyEmployee, setNotifyEmployee] = useState(true);
    const [notifyClient, setNotifyClient] = useState(true);
    const [notifyAssignment, setNotifyAssignment] = useState(true);
    const [notifyConfirmation, setNotifyConfirmation] = useState(true);
    const [notifyInvoice, setNotifyInvoice] = useState(true);
    const [employeeSubject, setEmployeeSubject] = useState('');
    const [employeeBody, setEmployeeBody] = useState('');
    const [clientSubject, setClientSubject] = useState('');
    const [clientBody, setClientBody] = useState('');
    const [assignmentSubject, setAssignmentSubject] = useState('');
    const [assignmentBody, setAssignmentBody] = useState('');
    const [confirmationSubject, setConfirmationSubject] = useState('');
    const [confirmationBody, setConfirmationBody] = useState('');
    const [invoicePaidSubject, setInvoicePaidSubject] = useState('');
    const [invoicePaidBody, setInvoicePaidBody] = useState('');
    const [invoicePartialSubject, setInvoicePartialSubject] = useState('');
    const [invoicePartialBody, setInvoicePartialBody] = useState('');
    const [invoiceUnpaidSubject, setInvoiceUnpaidSubject] = useState('');
    const [invoiceUnpaidBody, setInvoiceUnpaidBody] = useState('');
    const [bookingLinkSubject, setBookingLinkSubject] = useState('');
    const [bookingLinkBody, setBookingLinkBody] = useState('');
    const [newRequestSubject, setNewRequestSubject] = useState('');
    const [newRequestBody, setNewRequestBody] = useState('');
    const [docResponseSubject, setDocResponseSubject] = useState('');
    const [docResponseBody, setDocResponseBody] = useState('');
    const [docPaymentSubject, setDocPaymentSubject] = useState('');
    const [docPaymentBody, setDocPaymentBody] = useState('');
    const [notifyAbsenceEmployer, setNotifyAbsenceEmployer] = useState(true);
    const [absenceEmployerSubject, setAbsenceEmployerSubject] = useState('');
    const [absenceEmployerBody, setAbsenceEmployerBody] = useState('');
    // Une pièce jointe (PDF/image) optionnelle par type d'email — un seul fichier statique envoyé à chaque
    // envoi de cet email, clé = même identifiant que les colonnes ..._subject/..._body (employee, client,
    // assignment, confirmation, invoice_paid/partial/unpaid, booking_link, new_request, doc_response,
    // doc_payment, absence_employer).
    const [attachments, setAttachments] = useState<Record<string, NotificationAttachment | null>>({});
    const setAttachment = (type: string, att: NotificationAttachment | null) => setAttachments((prev) => ({ ...prev, [type]: att }));
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    // Champ actuellement ciblé (objet ou corps) par bloc, pour savoir où insérer le prochain clic sur un
    // bouton de variable — par défaut le corps, le plus probable.
    const [employeeActiveField, setEmployeeActiveField] = useState<'subject' | 'body'>('body');
    const [clientActiveField, setClientActiveField] = useState<'subject' | 'body'>('body');
    const [assignmentActiveField, setAssignmentActiveField] = useState<'subject' | 'body'>('body');
    const [confirmationActiveField, setConfirmationActiveField] = useState<'subject' | 'body'>('body');
    const [invoicePaidActiveField, setInvoicePaidActiveField] = useState<'subject' | 'body'>('body');
    const [invoicePartialActiveField, setInvoicePartialActiveField] = useState<'subject' | 'body'>('body');
    const [invoiceUnpaidActiveField, setInvoiceUnpaidActiveField] = useState<'subject' | 'body'>('body');
    const employeeSubjectRef = useRef<HTMLInputElement>(null);
    const employeeBodyRef = useRef<HTMLTextAreaElement>(null);
    const clientSubjectRef = useRef<HTMLInputElement>(null);
    const clientBodyRef = useRef<HTMLTextAreaElement>(null);
    const assignmentSubjectRef = useRef<HTMLInputElement>(null);
    const assignmentBodyRef = useRef<HTMLTextAreaElement>(null);
    const confirmationSubjectRef = useRef<HTMLInputElement>(null);
    const confirmationBodyRef = useRef<HTMLTextAreaElement>(null);
    const invoicePaidSubjectRef = useRef<HTMLInputElement>(null);
    const invoicePaidBodyRef = useRef<HTMLTextAreaElement>(null);
    const invoicePartialSubjectRef = useRef<HTMLInputElement>(null);
    const invoicePartialBodyRef = useRef<HTMLTextAreaElement>(null);
    const invoiceUnpaidSubjectRef = useRef<HTMLInputElement>(null);
    const invoiceUnpaidBodyRef = useRef<HTMLTextAreaElement>(null);
    const [bookingLinkActiveField, setBookingLinkActiveField] = useState<'subject' | 'body'>('body');
    const bookingLinkSubjectRef = useRef<HTMLInputElement>(null);
    const bookingLinkBodyRef = useRef<HTMLTextAreaElement>(null);
    const [newRequestActiveField, setNewRequestActiveField] = useState<'subject' | 'body'>('body');
    const newRequestSubjectRef = useRef<HTMLInputElement>(null);
    const newRequestBodyRef = useRef<HTMLTextAreaElement>(null);
    const [docResponseActiveField, setDocResponseActiveField] = useState<'subject' | 'body'>('body');
    const docResponseSubjectRef = useRef<HTMLInputElement>(null);
    const docResponseBodyRef = useRef<HTMLTextAreaElement>(null);
    const [docPaymentActiveField, setDocPaymentActiveField] = useState<'subject' | 'body'>('body');
    const docPaymentSubjectRef = useRef<HTMLInputElement>(null);
    const docPaymentBodyRef = useRef<HTMLTextAreaElement>(null);
    const [absenceEmployerActiveField, setAbsenceEmployerActiveField] = useState<'subject' | 'body'>('body');
    const absenceEmployerSubjectRef = useRef<HTMLInputElement>(null);
    const absenceEmployerBodyRef = useRef<HTMLTextAreaElement>(null);

    const insertEmployeeToken = (token: string) => {
        if (employeeActiveField === 'subject') insertNotificationToken(employeeSubjectRef.current, employeeSubject, setEmployeeSubject, token);
        else insertNotificationToken(employeeBodyRef.current, employeeBody, setEmployeeBody, token);
    };
    const insertClientToken = (token: string) => {
        if (clientActiveField === 'subject') insertNotificationToken(clientSubjectRef.current, clientSubject, setClientSubject, token);
        else insertNotificationToken(clientBodyRef.current, clientBody, setClientBody, token);
    };
    const insertAssignmentToken = (token: string) => {
        if (assignmentActiveField === 'subject') insertNotificationToken(assignmentSubjectRef.current, assignmentSubject, setAssignmentSubject, token);
        else insertNotificationToken(assignmentBodyRef.current, assignmentBody, setAssignmentBody, token);
    };
    const insertConfirmationToken = (token: string) => {
        if (confirmationActiveField === 'subject') insertNotificationToken(confirmationSubjectRef.current, confirmationSubject, setConfirmationSubject, token);
        else insertNotificationToken(confirmationBodyRef.current, confirmationBody, setConfirmationBody, token);
    };
    const insertInvoicePaidToken = (token: string) => {
        if (invoicePaidActiveField === 'subject') insertNotificationToken(invoicePaidSubjectRef.current, invoicePaidSubject, setInvoicePaidSubject, token);
        else insertNotificationToken(invoicePaidBodyRef.current, invoicePaidBody, setInvoicePaidBody, token);
    };
    const insertInvoicePartialToken = (token: string) => {
        if (invoicePartialActiveField === 'subject') insertNotificationToken(invoicePartialSubjectRef.current, invoicePartialSubject, setInvoicePartialSubject, token);
        else insertNotificationToken(invoicePartialBodyRef.current, invoicePartialBody, setInvoicePartialBody, token);
    };
    const insertInvoiceUnpaidToken = (token: string) => {
        if (invoiceUnpaidActiveField === 'subject') insertNotificationToken(invoiceUnpaidSubjectRef.current, invoiceUnpaidSubject, setInvoiceUnpaidSubject, token);
        else insertNotificationToken(invoiceUnpaidBodyRef.current, invoiceUnpaidBody, setInvoiceUnpaidBody, token);
    };
    const insertBookingLinkToken = (token: string) => {
        if (bookingLinkActiveField === 'subject') insertNotificationToken(bookingLinkSubjectRef.current, bookingLinkSubject, setBookingLinkSubject, token);
        else insertNotificationToken(bookingLinkBodyRef.current, bookingLinkBody, setBookingLinkBody, token);
    };
    const insertNewRequestToken = (token: string) => {
        if (newRequestActiveField === 'subject') insertNotificationToken(newRequestSubjectRef.current, newRequestSubject, setNewRequestSubject, token);
        else insertNotificationToken(newRequestBodyRef.current, newRequestBody, setNewRequestBody, token);
    };
    const insertDocResponseToken = (token: string) => {
        if (docResponseActiveField === 'subject') insertNotificationToken(docResponseSubjectRef.current, docResponseSubject, setDocResponseSubject, token);
        else insertNotificationToken(docResponseBodyRef.current, docResponseBody, setDocResponseBody, token);
    };
    const insertDocPaymentToken = (token: string) => {
        if (docPaymentActiveField === 'subject') insertNotificationToken(docPaymentSubjectRef.current, docPaymentSubject, setDocPaymentSubject, token);
        else insertNotificationToken(docPaymentBodyRef.current, docPaymentBody, setDocPaymentBody, token);
    };
    const insertAbsenceEmployerToken = (token: string) => {
        if (absenceEmployerActiveField === 'subject') insertNotificationToken(absenceEmployerSubjectRef.current, absenceEmployerSubject, setAbsenceEmployerSubject, token);
        else insertNotificationToken(absenceEmployerBodyRef.current, absenceEmployerBody, setAbsenceEmployerBody, token);
    };

    const wrapEmployeeBody = (marker: string) => wrapNotificationSelection(employeeBodyRef.current, employeeBody, setEmployeeBody, marker);
    const wrapClientBody = (marker: string) => wrapNotificationSelection(clientBodyRef.current, clientBody, setClientBody, marker);
    const wrapAssignmentBody = (marker: string) => wrapNotificationSelection(assignmentBodyRef.current, assignmentBody, setAssignmentBody, marker);
    const wrapConfirmationBody = (marker: string) => wrapNotificationSelection(confirmationBodyRef.current, confirmationBody, setConfirmationBody, marker);
    const wrapInvoicePaidBody = (marker: string) => wrapNotificationSelection(invoicePaidBodyRef.current, invoicePaidBody, setInvoicePaidBody, marker);
    const wrapInvoicePartialBody = (marker: string) => wrapNotificationSelection(invoicePartialBodyRef.current, invoicePartialBody, setInvoicePartialBody, marker);
    const wrapInvoiceUnpaidBody = (marker: string) => wrapNotificationSelection(invoiceUnpaidBodyRef.current, invoiceUnpaidBody, setInvoiceUnpaidBody, marker);
    const wrapBookingLinkBody = (marker: string) => wrapNotificationSelection(bookingLinkBodyRef.current, bookingLinkBody, setBookingLinkBody, marker);
    const wrapNewRequestBody = (marker: string) => wrapNotificationSelection(newRequestBodyRef.current, newRequestBody, setNewRequestBody, marker);
    const wrapDocResponseBody = (marker: string) => wrapNotificationSelection(docResponseBodyRef.current, docResponseBody, setDocResponseBody, marker);
    const wrapDocPaymentBody = (marker: string) => wrapNotificationSelection(docPaymentBodyRef.current, docPaymentBody, setDocPaymentBody, marker);
    const wrapAbsenceEmployerBody = (marker: string) => wrapNotificationSelection(absenceEmployerBodyRef.current, absenceEmployerBody, setAbsenceEmployerBody, marker);

    useEffect(() => {
        moduleApi.getPrestationNotificationSettings().then((s: any) => {
            setNotifyEmployee(s?.notify_employee !== false);
            setNotifyClient(s?.notify_client !== false);
            setNotifyAssignment(s?.notify_assignment !== false);
            setNotifyConfirmation(s?.notify_confirmation !== false);
            setNotifyInvoice(s?.notify_invoice !== false);
            setEmployeeSubject(s?.employee_subject || '');
            setEmployeeBody(s?.employee_body || '');
            setClientSubject(s?.client_subject || '');
            setClientBody(s?.client_body || '');
            setAssignmentSubject(s?.assignment_subject || '');
            setAssignmentBody(s?.assignment_body || '');
            setConfirmationSubject(s?.confirmation_subject || '');
            setConfirmationBody(s?.confirmation_body || '');
            setInvoicePaidSubject(s?.invoice_paid_subject || '');
            setInvoicePaidBody(s?.invoice_paid_body || '');
            setInvoicePartialSubject(s?.invoice_partial_subject || '');
            setInvoicePartialBody(s?.invoice_partial_body || '');
            setInvoiceUnpaidSubject(s?.invoice_unpaid_subject || '');
            setInvoiceUnpaidBody(s?.invoice_unpaid_body || '');
            setBookingLinkSubject(s?.booking_link_subject || '');
            setBookingLinkBody(s?.booking_link_body || '');
            setNewRequestSubject(s?.new_request_subject || '');
            setNewRequestBody(s?.new_request_body || '');
            setDocResponseSubject(s?.doc_response_subject || '');
            setDocResponseBody(s?.doc_response_body || '');
            setDocPaymentSubject(s?.doc_payment_subject || '');
            setDocPaymentBody(s?.doc_payment_body || '');
            setNotifyAbsenceEmployer(s?.notify_absence_employer !== false);
            setAbsenceEmployerSubject(s?.absence_employer_subject || '');
            setAbsenceEmployerBody(s?.absence_employer_body || '');
            setAttachments(s?.attachments || {});
        }).catch((e) => console.error('Failed to load notification settings', e)).finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            const result: any = await moduleApi.savePrestationNotificationSettings({
                notify_employee: notifyEmployee, notify_client: notifyClient,
                notify_assignment: notifyAssignment, notify_confirmation: notifyConfirmation, notify_invoice: notifyInvoice,
                notify_absence_employer: notifyAbsenceEmployer,
                employee_subject: employeeSubject, employee_body: employeeBody,
                client_subject: clientSubject, client_body: clientBody,
                assignment_subject: assignmentSubject, assignment_body: assignmentBody,
                confirmation_subject: confirmationSubject, confirmation_body: confirmationBody,
                invoice_paid_subject: invoicePaidSubject, invoice_paid_body: invoicePaidBody,
                invoice_partial_subject: invoicePartialSubject, invoice_partial_body: invoicePartialBody,
                invoice_unpaid_subject: invoiceUnpaidSubject, invoice_unpaid_body: invoiceUnpaidBody,
                booking_link_subject: bookingLinkSubject, booking_link_body: bookingLinkBody,
                new_request_subject: newRequestSubject, new_request_body: newRequestBody,
                doc_response_subject: docResponseSubject, doc_response_body: docResponseBody,
                doc_payment_subject: docPaymentSubject, doc_payment_body: docPaymentBody,
                absence_employer_subject: absenceEmployerSubject, absence_employer_body: absenceEmployerBody,
                attachments,
            });
            if (result) {
                setAttachments(result.attachments || {});
                setNotifyAssignment(result.notify_assignment !== false);
                setNotifyConfirmation(result.notify_confirmation !== false);
                setNotifyInvoice(result.notify_invoice !== false);
                setEmployeeSubject(result.employee_subject || '');
                setEmployeeBody(result.employee_body || '');
                setClientSubject(result.client_subject || '');
                setClientBody(result.client_body || '');
                setAssignmentSubject(result.assignment_subject || '');
                setAssignmentBody(result.assignment_body || '');
                setConfirmationSubject(result.confirmation_subject || '');
                setConfirmationBody(result.confirmation_body || '');
                setInvoicePaidSubject(result.invoice_paid_subject || '');
                setInvoicePaidBody(result.invoice_paid_body || '');
                setInvoicePartialSubject(result.invoice_partial_subject || '');
                setInvoicePartialBody(result.invoice_partial_body || '');
                setInvoiceUnpaidSubject(result.invoice_unpaid_subject || '');
                setInvoiceUnpaidBody(result.invoice_unpaid_body || '');
                setBookingLinkSubject(result.booking_link_subject || '');
                setBookingLinkBody(result.booking_link_body || '');
                setNewRequestSubject(result.new_request_subject || '');
                setNewRequestBody(result.new_request_body || '');
                setDocResponseSubject(result.doc_response_subject || '');
                setDocResponseBody(result.doc_response_body || '');
                setDocPaymentSubject(result.doc_payment_subject || '');
                setDocPaymentBody(result.doc_payment_body || '');
                setNotifyAbsenceEmployer(result.notify_absence_employer !== false);
                setAbsenceEmployerSubject(result.absence_employer_subject || '');
                setAbsenceEmployerBody(result.absence_employer_body || '');
            }
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e) {
            console.error('Failed to save notification settings', e);
        } finally {
            setSaving(false);
        }
    };

    const tabButton = (active: boolean) =>
        `flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${active ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`;

    return (
        <div className="space-y-6">
            {/* Bouton en ligne (pas flottant) — un bouton fixe à l'écran devenait ambigu dès que cette page
                est montée en même temps que ClientEmailNotificationsSettings (client Prestation avec les
                modules Facture/CRM/Congés aussi actifs) : deux boutons "Enregistrer" apparaissaient alors,
                pour deux formulaires différents, sans qu'on sache lequel enregistrait quoi. */}
            <button onClick={handleSave} disabled={saving}
                className="px-6 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold shadow-lg disabled:opacity-50">
                {saving ? 'Enregistrement...' : saved ? 'Enregistré ✓' : 'Enregistrer'}
            </button>

            <div className="bg-surface border border-border rounded-2xl p-2 inline-flex gap-2">
                <button onClick={() => setRecipient('client')} className={tabButton(recipient === 'client')}>
                    <User size={14} /> Client
                </button>
                <button onClick={() => setRecipient('salarie')} className={tabButton(recipient === 'salarie')}>
                    <UsersIcon size={14} /> Salarié
                </button>
                <button onClick={() => setRecipient('etablissement')} className={tabButton(recipient === 'etablissement')}>
                    <Building2 size={14} /> Établissement
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center py-24"><Loader2 className="animate-spin text-content-muted" size={28} /></div>
            ) : recipient === 'salarie' ? (
                <div className="max-w-3xl space-y-6">
                    <div>
                        <h2 className="text-xs font-black uppercase tracking-wider text-content-muted mb-1">Rappel avant rendez-vous</h2>
                        <p className="text-xs text-content-muted italic">Envoyé manuellement via le bouton "Envoyer un rappel" sur la fiche du rendez-vous.</p>
                    </div>
                    <NotificationBlock title="Mail de rappel au(x) salarié(s) assigné(s)"
                        toggle={{ checked: notifyEmployee, onChange: setNotifyEmployee }}
                        subject={employeeSubject} setSubject={setEmployeeSubject} body={employeeBody} setBody={setEmployeeBody}
                        subjectRef={employeeSubjectRef} bodyRef={employeeBodyRef}
                        activeField={employeeActiveField} setActiveField={setEmployeeActiveField}
                        onInsertToken={insertEmployeeToken} onWrap={wrapEmployeeBody}
                        attachment={attachments.employee} onAttachmentChange={(att) => setAttachment('employee', att)} />

                    <div className="pt-2">
                        <h2 className="text-xs font-black uppercase tracking-wider text-content-muted mb-1">Premier email (à la création du rendez-vous)</h2>
                        <p className="text-xs text-content-muted italic">Envoyé automatiquement dès qu'un salarié est assigné à un rendez-vous.</p>
                    </div>
                    <NotificationBlock title="Mail de nouveau rendez-vous au(x) salarié(s) assigné(s)"
                        toggle={{ checked: notifyAssignment, onChange: setNotifyAssignment }}
                        subject={assignmentSubject} setSubject={setAssignmentSubject} body={assignmentBody} setBody={setAssignmentBody}
                        subjectRef={assignmentSubjectRef} bodyRef={assignmentBodyRef}
                        activeField={assignmentActiveField} setActiveField={setAssignmentActiveField}
                        onInsertToken={insertAssignmentToken} onWrap={wrapAssignmentBody}
                        attachment={attachments.assignment} onAttachmentChange={(att) => setAttachment('assignment', att)} />
                </div>
            ) : recipient === 'etablissement' ? (
                <div className="max-w-3xl space-y-6">
                    <div>
                        <h2 className="text-xs font-black uppercase tracking-wider text-content-muted mb-1">Nouvelle demande de rendez-vous</h2>
                        <p className="text-xs text-content-muted italic">Envoyé dès qu'un client demande un rendez-vous — via une Démarche client ou le lien de réservation en ligne. Le rendez-vous reste en attente tant qu'aucun salarié n'est assigné depuis le Planning.</p>
                    </div>
                    <NotificationBlock title="Mail de nouvelle demande de rendez-vous"
                        subject={newRequestSubject} setSubject={setNewRequestSubject} body={newRequestBody} setBody={setNewRequestBody}
                        subjectRef={newRequestSubjectRef} bodyRef={newRequestBodyRef}
                        activeField={newRequestActiveField} setActiveField={setNewRequestActiveField}
                        onInsertToken={insertNewRequestToken} onWrap={wrapNewRequestBody}
                        attachment={attachments.new_request} onAttachmentChange={(att) => setAttachment('new_request', att)} />

                    {showDemarches && (
                        <>
                            <div className="pt-2">
                                <h2 className="text-xs font-black uppercase tracking-wider text-content-muted mb-1">Nouvelle réponse à une Démarche client</h2>
                                <p className="text-xs text-content-muted italic">Envoyé dès qu'un client répond à une Démarche que vous lui avez envoyée (formulaire, choix, paiement).</p>
                            </div>
                            <NotificationBlock title="Mail de nouvelle réponse reçue"
                                subject={docResponseSubject} setSubject={setDocResponseSubject} body={docResponseBody} setBody={setDocResponseBody}
                                subjectRef={docResponseSubjectRef} bodyRef={docResponseBodyRef}
                                activeField={docResponseActiveField} setActiveField={setDocResponseActiveField}
                                onInsertToken={insertDocResponseToken} onWrap={wrapDocResponseBody}
                                attachment={attachments.doc_response} onAttachmentChange={(att) => setAttachment('doc_response', att)} />

                            <div className="pt-2">
                                <h2 className="text-xs font-black uppercase tracking-wider text-content-muted mb-1">Paiement reçu (Démarche client)</h2>
                                <p className="text-xs text-content-muted italic">Envoyé dès qu'un paiement lié à une Démarche client est marqué comme reçu.</p>
                            </div>
                            <NotificationBlock title="Mail de paiement reçu"
                                subject={docPaymentSubject} setSubject={setDocPaymentSubject} body={docPaymentBody} setBody={setDocPaymentBody}
                                subjectRef={docPaymentSubjectRef} bodyRef={docPaymentBodyRef}
                                activeField={docPaymentActiveField} setActiveField={setDocPaymentActiveField}
                                onInsertToken={insertDocPaymentToken} onWrap={wrapDocPaymentBody}
                                attachment={attachments.doc_payment} onAttachmentChange={(att) => setAttachment('doc_payment', att)} />
                        </>
                    )}

                    {showConges && (
                        <>
                            <div className="pt-2">
                                <h2 className="text-xs font-black uppercase tracking-wider text-content-muted mb-1">Nouvelle demande d'absence</h2>
                                <p className="text-xs text-content-muted italic">Envoyé dès qu'un salarié soumet une nouvelle demande d'absence/congé.</p>
                            </div>
                            <NotificationBlock title="Mail de nouvelle demande d'absence"
                                toggle={{ checked: notifyAbsenceEmployer, onChange: setNotifyAbsenceEmployer }}
                                subject={absenceEmployerSubject} setSubject={setAbsenceEmployerSubject} body={absenceEmployerBody} setBody={setAbsenceEmployerBody}
                                subjectRef={absenceEmployerSubjectRef} bodyRef={absenceEmployerBodyRef}
                                activeField={absenceEmployerActiveField} setActiveField={setAbsenceEmployerActiveField}
                                onInsertToken={insertAbsenceEmployerToken} onWrap={wrapAbsenceEmployerBody}
                                attachment={attachments.absence_employer} onAttachmentChange={(att) => setAttachment('absence_employer', att)} />
                        </>
                    )}
                </div>
            ) : (
                <div className="max-w-3xl space-y-6">
                    <div>
                        <h2 className="text-xs font-black uppercase tracking-wider text-content-muted mb-1">Rappel avant rendez-vous</h2>
                        <p className="text-xs text-content-muted italic">Envoyé manuellement via le bouton "Envoyer un rappel" sur la fiche du rendez-vous.</p>
                    </div>
                    <NotificationBlock title="Mail de rappel au client final"
                        toggle={{ checked: notifyClient, onChange: setNotifyClient }}
                        subject={clientSubject} setSubject={setClientSubject} body={clientBody} setBody={setClientBody}
                        subjectRef={clientSubjectRef} bodyRef={clientBodyRef}
                        activeField={clientActiveField} setActiveField={setClientActiveField}
                        onInsertToken={insertClientToken} onWrap={wrapClientBody}
                        attachment={attachments.client} onAttachmentChange={(att) => setAttachment('client', att)} />

                    <div className="pt-2">
                        <h2 className="text-xs font-black uppercase tracking-wider text-content-muted mb-1">Premier email (à la création du rendez-vous)</h2>
                        <p className="text-xs text-content-muted italic">Envoyé automatiquement dès qu'un rendez-vous est saisi.</p>
                    </div>
                    <NotificationBlock title="Mail de confirmation au client final"
                        toggle={{ checked: notifyConfirmation, onChange: setNotifyConfirmation }}
                        subject={confirmationSubject} setSubject={setConfirmationSubject} body={confirmationBody} setBody={setConfirmationBody}
                        subjectRef={confirmationSubjectRef} bodyRef={confirmationBodyRef}
                        activeField={confirmationActiveField} setActiveField={setConfirmationActiveField}
                        onInsertToken={insertConfirmationToken} onWrap={wrapConfirmationBody}
                        attachment={attachments.confirmation} onAttachmentChange={(att) => setAttachment('confirmation', att)} />

                    <div className="pt-2">
                        <h2 className="text-xs font-black uppercase tracking-wider text-content-muted mb-1 flex items-center gap-1.5"><UsersIcon size={12} /> Lien de rendez-vous en ligne</h2>
                        <p className="text-xs text-content-muted italic">Envoyé quand vous cliquez sur "Envoyer un lien de rendez-vous" (Planning) pour inviter un client à réserver lui-même son créneau.</p>
                    </div>
                    <NotificationBlock title="Mail d'invitation à réserver en ligne"
                        subject={bookingLinkSubject} setSubject={setBookingLinkSubject} body={bookingLinkBody} setBody={setBookingLinkBody}
                        subjectRef={bookingLinkSubjectRef} bodyRef={bookingLinkBodyRef}
                        activeField={bookingLinkActiveField} setActiveField={setBookingLinkActiveField}
                        onInsertToken={insertBookingLinkToken} onWrap={wrapBookingLinkBody}
                        attachment={attachments.booking_link} onAttachmentChange={(att) => setAttachment('booking_link', att)} />

                    <div className="pt-2">
                        <h2 className="text-xs font-black uppercase tracking-wider text-content-muted mb-1 flex items-center gap-1.5"><Receipt size={12} /> Fin de prestation</h2>
                        <p className="text-xs text-content-muted italic">
                            Envoyé automatiquement dès que la prestation est clôturée. Le corps du mail est différent selon que la prestation est soldée, partiellement réglée, ou pas réglée du tout.
                            {showFacture ? ' La facture PDF est jointe automatiquement (module Facture actif).' : ' Le module Facture n\'étant pas activé, cet email part sans facture PDF jointe.'}
                        </p>
                    </div>
                    <label className="flex items-center justify-between cursor-pointer p-4 rounded-2xl bg-surface border border-border">
                        <span className="text-sm font-bold text-content">Activer cet email</span>
                        <input type="checkbox" checked={notifyInvoice} onChange={(e) => setNotifyInvoice(e.target.checked)} className="accent-brand-600 w-4 h-4" />
                    </label>
                    <NotificationBlock title="Prestation soldée (payée en intégralité)"
                        subject={invoicePaidSubject} setSubject={setInvoicePaidSubject} body={invoicePaidBody} setBody={setInvoicePaidBody}
                        subjectRef={invoicePaidSubjectRef} bodyRef={invoicePaidBodyRef}
                        activeField={invoicePaidActiveField} setActiveField={setInvoicePaidActiveField}
                        onInsertToken={insertInvoicePaidToken} onWrap={wrapInvoicePaidBody}
                        attachment={attachments.invoice_paid} onAttachmentChange={(att) => setAttachment('invoice_paid', att)} />
                    <NotificationBlock title="Prestation partiellement réglée (solde restant)"
                        subject={invoicePartialSubject} setSubject={setInvoicePartialSubject} body={invoicePartialBody} setBody={setInvoicePartialBody}
                        subjectRef={invoicePartialSubjectRef} bodyRef={invoicePartialBodyRef}
                        activeField={invoicePartialActiveField} setActiveField={setInvoicePartialActiveField}
                        onInsertToken={insertInvoicePartialToken} onWrap={wrapInvoicePartialBody}
                        attachment={attachments.invoice_partial} onAttachmentChange={(att) => setAttachment('invoice_partial', att)} />
                    <NotificationBlock title="Prestation non réglée (rien payé)"
                        subject={invoiceUnpaidSubject} setSubject={setInvoiceUnpaidSubject} body={invoiceUnpaidBody} setBody={setInvoiceUnpaidBody}
                        subjectRef={invoiceUnpaidSubjectRef} bodyRef={invoiceUnpaidBodyRef}
                        activeField={invoiceUnpaidActiveField} setActiveField={setInvoiceUnpaidActiveField}
                        onInsertToken={insertInvoiceUnpaidToken} onWrap={wrapInvoiceUnpaidBody}
                        attachment={attachments.invoice_unpaid} onAttachmentChange={(att) => setAttachment('invoice_unpaid', att)} />
                </div>
            )}
        </div>
    );
};
